import type { AccountType } from "@cosmosai/shared";
import type { LucideIcon } from "lucide-react";
import { createRegistry } from "./registry";

/** Sidebar groups in display order (Studio spec §3.2); `account` entries go to the user menu. */
export const navGroups = [
  "home",
  "work",
  "build",
  "resources",
  "deliver",
  "monitor",
  "admin",
  "account",
] as const;

export type NavGroup = (typeof navGroups)[number];

/** A route of the router with its params and search, as Link and navigate take them. */
export interface RouteTarget {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
}

/** Where the user is: the current route's params and search. */
export interface NavLocation {
  params: Record<string, string | undefined>;
  search: Record<string, unknown>;
}

/** A fixed path, or one built from where the user is (null: not available there). */
export type NavTarget = string | ((at: NavLocation) => RouteTarget | null);

/** The route a target leads to from `at`, or null when it is not available there. */
export const resolveTarget = (target: NavTarget, at: NavLocation): RouteTarget | null =>
  typeof target === "string" ? { to: target } : target(at);

export interface NavEntry {
  id: string;
  group: NavGroup;
  /** i18n key of the label. */
  labelKey: string;
  icon: LucideIcon;
  /** A route path of the router, or a function of the current location. */
  to: NavTarget;
  order: number;
  /** A count shown next to the label, such as items waiting. */
  badge?: () => number | undefined;
  /** The account types that see the entry (display only, the APIs enforce); everyone if absent. */
  visibleTo?: readonly AccountType[];
}

/** The entries an account type sees. */
export const visibleFor = (entries: NavEntry[], type: AccountType): NavEntry[] =>
  entries.filter((entry) => !entry.visibleTo || entry.visibleTo.includes(type));

const groupRank = new Map<NavGroup, number>(navGroups.map((group, rank) => [group, rank]));

/** Entries sorted by group, then by their order within it. */
export const sortNav = (entries: NavEntry[]): NavEntry[] =>
  [...entries].sort(
    (a, b) =>
      (groupRank.get(a.group) ?? 0) - (groupRank.get(b.group) ?? 0) ||
      a.order - b.order ||
      a.id.localeCompare(b.id),
  );

/** A navigation registry; the app has one, tests make their own. */
export function createNavRegistry() {
  const registry = createRegistry<NavEntry>("navigation");
  return { register: registry.register, entries: () => sortNav(registry.list()) };
}

const nav = createNavRegistry();

/** Adds a navigation entry; throws on a duplicate id. */
export const registerNav = (entry: NavEntry): void => nav.register(entry);

/** Every registered entry, sorted by group then order. */
export const navEntries = (): NavEntry[] => nav.entries();
